import { useMemo, useState } from 'react';
import CampaignGroup from './components/CampaignGroup';
import CampaignAdGroupsTable from './components/CampaignAdGroupsTable';
import CampaignAdsTable from './components/CampaignAdsTable';
import AdPreviewModal from './components/AdPreviewModal';

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1 text-sm ${
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function ColorLegend() {
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-gray-400">
      Escala por grupo
      <span className="inline-block h-2.5 w-3 rounded-sm bg-red-100" />
      <span className="inline-block h-2.5 w-3 rounded-sm bg-amber-100" />
      <span className="inline-block h-2.5 w-3 rounded-sm bg-emerald-100" />
      peor → mejor
    </div>
  );
}

/**
 * Sección "Campañas y anuncios": dos vistas (campañas agrupadas por objetivo /
 * anuncios), estilo tabla densa. `data` es el payload de get_campaigns_section;
 * el fetch (admin vs cliente) lo hace el panel que la monta.
 */
export default function CampaignsSection({ data, isLoading, isError, vertical, previewFetcher }) {
  const [tab, setTab] = useState('campaigns');
  const [previewAd, setPreviewAd] = useState(null);
  const groups = data?.groups ?? [];
  const ads = data?.ads ?? [];
  const hasData = groups.length > 0 || ads.length > 0;

  // Vista plana del nivel intermedio: los conjuntos anidados en cada campaña,
  // taggeados con el objetivo del grupo + el nombre de su campaña padre.
  const adGroups = useMemo(() => {
    const out = [];
    (data?.groups ?? []).forEach((g) => {
      g.campaigns.forEach((c) => {
        (c.ad_groups ?? []).forEach((ag) => {
          out.push({ ...ag, objective_group: g.objective_group, campaign_name: c.campaign_name });
        });
      });
    });
    return out;
  }, [data]);

  return (
    <section className="space-y-4">
      <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1">
        <TabButton active={tab === 'campaigns'} onClick={() => setTab('campaigns')}>
          Campañas
        </TabButton>
        <TabButton active={tab === 'adgroups'} onClick={() => setTab('adgroups')}>
          Conjuntos ({adGroups.length})
        </TabButton>
        <TabButton active={tab === 'ads'} onClick={() => setTab('ads')}>
          Anuncios ({ads.length})
        </TabButton>
      </div>

      {isLoading && <p className="py-8 text-center text-sm text-gray-500">Cargando campañas…</p>}
      {isError && (
        <p className="py-8 text-center text-sm text-red-600">No se pudieron cargar las campañas.</p>
      )}
      {!isLoading && !isError && !hasData && (
        <p className="py-8 text-center text-sm text-gray-500">
          Sin campañas con actividad en el rango seleccionado.
        </p>
      )}

      {!isLoading && !isError && hasData && tab === 'campaigns' && (
        <div className="space-y-3">
          {groups.map((g, i) => (
            <CampaignGroup
              key={g.objective_group}
              group={g}
              ads={ads}
              defaultOpen={i === 0}
              onPreview={setPreviewAd}
            />
          ))}
          <ColorLegend />
        </div>
      )}
      {!isLoading && !isError && hasData && tab === 'adgroups' && (
        <CampaignAdGroupsTable adGroups={adGroups} vertical={vertical} />
      )}
      {!isLoading && !isError && hasData && tab === 'ads' && (
        <CampaignAdsTable ads={ads} vertical={vertical} onPreview={setPreviewAd} />
      )}

      {previewAd && (
        <AdPreviewModal ad={previewAd} fetchPreview={previewFetcher} onClose={() => setPreviewAd(null)} />
      )}
    </section>
  );
}
